import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { triggerSessionExpired } from "./sessionGuard";

const BASE = process.env.REACT_APP_BASE_URL;

export const customInstance = async <T = unknown>(
  config: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  const instance = axios.create({
    baseURL: BASE,
    withCredentials: true,
  });

  let attempt = 0;
  const maxRetries = 1; // Only retry once for 401
  const retryDelay = 500; // ms

  while (attempt <= maxRetries) {
    try {
      const res = await instance(config);
      return res.data;
    } catch (e) {
      const err = e as AxiosError;
      let path = String(config.url || "");
      try {
        path = new URL(path, BASE).pathname;
      } catch {
        /* ignore */
      }

      const skip =
        path.startsWith("/users/current_user") ||
        path.startsWith("/authentication/programmatic-login");

      const status = err.response?.status ?? 0;
      const code = err.code ?? "";

      // Only retry for 401, not for skip paths
      if (
        !skip &&
        attempt < maxRetries &&
        (status === 401 || status === 419 || code === "ERR_NETWORK")
      ) {
        attempt++;
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        continue; // Retry the request
      }

      // If not retrying, or after retry, trigger session expired
      if (!skip && (status === 401 || status === 419 || code === "ERR_NETWORK")) {
        triggerSessionExpired("SESSION_EXPIRED");
      }

      throw e;
    }
  }
  // Add a fallback return to satisfy the return type
  throw new Error("customInstance: No response returned.");
};